import { useMemo } from 'react';

import { SectionTabs, withCount } from '~/components/common/SectionTabs';
import { ElevatorInvoicesTable } from '~/components/invoices/detailPage/related/ElevatorInvoicesTable';
import { ElevatorsServicedSection } from '~/components/invoices/detailPage/related/ElevatorsServicedSection';
import { ElevatorTicketsTable } from '~/components/invoices/detailPage/related/ElevatorTicketsTable';
import { useRelatedCounts } from '~/components/invoices/detailPage/useRelatedCounts';
import { type Invoice, InvoiceKind } from '~/data/schemas/invoice';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';

/**
 * The elevators this invoice serviced, and what else has been raised against them:
 * their full ticket history, every other invoice billed to them, and every proposal
 * still open on them. All four views are about the same elevators, which is why they
 * share a tab rather than doubling the Linked Tickets and Linked Invoices tabs.
 */
export function CarsServicedSection({ invoice }: { invoice: Invoice }) {
  const terms = useLocaleTerms();
  const carIds = useMemo(() => invoice.carsServiced.map((car) => car.id), [invoice.carsServiced]);
  // Same hook the tab strip's total comes from — and the same SWR keys, so no
  // extra requests.
  const counts = useRelatedCounts(invoice, undefined);
  const nothingToCompare = `This invoice lists no serviced ${terms.vehicles.toLowerCase()}, so there is nothing to compare it against.`;

  return (
    <SectionTabs
      ariaLabel={`${terms.vehiclesServiced} views`}
      // The subtab labels are bare nouns; without this the tickets and invoices they list
      // read as the invoice's own rather than the serviced elevators'.
      helperText={`Records for the ${terms.vehicles.toLowerCase()} this invoice serviced`}
      tabs={[
        {
          value: 'cars',
          label: withCount(terms.vehicles, counts.carsServiced),
          render: () => <ElevatorsServicedSection invoice={invoice} />,
        },
        {
          value: 'tickets',
          label: withCount('Tickets', counts.servicedTickets),
          render: () => (
            <ElevatorTicketsTable
              previewList="invoice_car_tickets"
              carIds={carIds}
              emptyMessage={`This invoice lists no serviced ${terms.vehicles.toLowerCase()}, so there is no history to show.`}
            />
          ),
        },
        // Invoices and proposals get a subtab each rather than one list with a Class filter:
        // what we have already been billed for and what is still only quoted are different
        // questions, and each label carries its own count.
        {
          value: 'invoices',
          label: withCount('Invoices', counts.servicedInvoices),
          render: () => (
            <ElevatorInvoicesTable
              carIds={carIds}
              relatedTo={invoice}
              kind={InvoiceKind.Invoice}
              emptyMessage={nothingToCompare}
            />
          ),
        },
        {
          value: 'proposals',
          label: withCount('Proposals', counts.servicedProposals),
          render: () => (
            <ElevatorInvoicesTable
              carIds={carIds}
              relatedTo={invoice}
              kind={InvoiceKind.Proposal}
              emptyMessage={nothingToCompare}
            />
          ),
        },
      ]}
    />
  );
}
